import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage, useApi } from "@/lib/api";
import type { Group, Playlist, Screen } from "@/lib/types";
import { ConfigEditor } from "@/components/app/ConfigEditor";
import { ConfirmAction } from "@/components/app/ConfirmAction";
import { Loading, PageHeader } from "@/components/app/PageHeader";
import { GroupScreensPanel } from "@/components/app/GroupScreensPanel";

export default function GroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const group = useApi<Group>(`/groups/${id}`);
  const playlists = useApi<Playlist[]>("/playlists");
  const screens = useApi<Screen[]>("/screens");
  const [draft, setDraft] = useState<Group | null>(null);

  useEffect(() => { if (group.data) setDraft(group.data); }, [group.data]);
  if (!draft) return group.error ? <p className="text-rose-300">{group.error}</p> : <Loading />;

  const save = async () => {
    try {
      await api.patch(`/groups/${id}`, { name: draft.name, description: draft.description, config: draft.config });
      toast.success("Groupe enregistré — les écrans liés recevront le nouveau manifest");
      group.reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async () => { try { await api.delete(`/groups/${id}`); toast.success("Groupe supprimé"); navigate("/groupes"); } catch (e) { toast.error(errorMessage(e)); } };

  return (
    <div>
      <PageHeader eyebrow="Groupe d'écrans" title={group.data?.name || ""}
        actions={<ConfirmAction testId="group-delete" title="Supprimer ce groupe ?" description="Les écrans du groupe reviendront à leur propre configuration." confirmLabel="Supprimer" onConfirm={remove}
          trigger={<Button data-testid="btn-delete-group" variant="ghost" className="text-slate-400 hover:text-rose-300"><Trash2 className="mr-1 h-4 w-4" />Supprimer</Button>} />} />
      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <div className="panel grid gap-5 p-6 md:grid-cols-2">
            <div className="space-y-2"><Label>Nom</Label><Input data-testid="group-detail-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
            <div className="space-y-2"><Label>Description</Label><Input data-testid="group-detail-description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
          </div>
          <div className="panel p-6"><ConfigEditor value={draft.config} onChange={(c) => setDraft({ ...draft, config: c })} playlists={playlists.data || []} /></div>
          <div className="flex justify-end"><Button data-testid="btn-save-group" onClick={save} className="bg-blue-600 text-white hover:bg-blue-500"><Save className="mr-1 h-4 w-4" />Enregistrer</Button></div>
        </div>
        <div className="panel h-fit p-6">
          <GroupScreensPanel groupId={id!} screens={screens.data || []} onChange={screens.reload} />
        </div>
      </div>
    </div>
  );
}
